import { PageShell, LegalSection } from "./PageShell";

export default function TermsPage() {
  return (
    <PageShell eyebrow="841 wellness" title="Terms of Service">
      <p className="text-xs text-[#8A8079]" data-testid="terms-updated">
        Effective date: July 2026
      </p>

      <LegalSection title="Agreement" testId="terms-section-agreement">
        <p>
          These Terms of Service explain the relationship between 841 Wellness ("841", "we", "us")
          and you. By using this website, joining the waitlist, or using the 841 app when it
          becomes available to you, you agree to these terms.
        </p>
      </LegalSection>

      <LegalSection title="Nature of the service" testId="terms-section-nature">
        <p>841 Wellness provides general wellness support across:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Strength</li>
          <li>Nourishment</li>
          <li>Balance</li>
          <li>Self and reflection</li>
          <li>Personalised wellness guidance</li>
          <li>AI-assisted coaching</li>
          <li>Wearable and health-data-based insights, where integrations are enabled</li>
        </ul>
        <p>841 is a wellness service. It is not:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>A medical provider</li>
          <li>A doctor</li>
          <li>A therapist</li>
          <li>A registered dietitian</li>
          <li>Emergency medical care</li>
          <li>A replacement for professional medical advice, diagnosis or treatment</li>
        </ul>
      </LegalSection>

      <LegalSection title="Health and wellness disclaimer" testId="terms-section-disclaimer">
        <p>
          Recommendations provided by 841 are general wellness guidance. You are responsible for
          deciding whether an activity or recommendation is appropriate for you. Exercise carries
          inherent risks. Please seek appropriate professional advice whenever you have medical
          concerns, injuries, symptoms or conditions that require clinical care.
        </p>
        <p>
          841 should never be used in an emergency. The AI coach is not a medical professional and
          should not be treated as one.
        </p>
      </LegalSection>

      <LegalSection title="Exercise" testId="terms-section-exercise">
        <p>
          Physical activity carries potential risks, and by using 841's training features you
          acknowledge this. We encourage you to:
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Exercise within your capabilities.</li>
          <li>Stop if you experience concerning symptoms.</li>
          <li>Seek professional medical advice when appropriate.</li>
          <li>Follow any medical or professional restrictions you have been given.</li>
        </ul>
      </LegalSection>

      <LegalSection title="Nutrition" testId="terms-section-nutrition">
        <p>
          Nutrition information provided by 841 is intended for general wellness purposes. 841 does
          not diagnose, treat or cure medical conditions through food or nutrition recommendations.
        </p>
        <p>
          Where you share dietary restrictions, allergies or health considerations, the app makes
          reasonable efforts to personalise recommendations — but it cannot guarantee that every
          recommendation is medically suitable for you. When in doubt, please consult a qualified
          professional.
        </p>
      </LegalSection>

      <LegalSection
        title="Cycle, menopause and women's wellness information"
        testId="terms-section-cycle"
      >
        <p>
          841 may provide cycle-aware and life-stage-aware wellness guidance. This is wellness
          guidance, not medical diagnosis or treatment. Cycle information, menopause information
          and AI analysis cannot replace professional medical care — please speak to a qualified
          healthcare professional about any medical concerns.
        </p>
      </LegalSection>

      <LegalSection title="AI-generated content" testId="terms-section-ai">
        <p>
          Some recommendations, conversations, insights and content within 841 may be generated or
          assisted by AI. AI-generated information can be incomplete, inaccurate or inappropriate
          in some circumstances, so please use your own judgment and seek professional advice where
          something matters to your health.
        </p>
      </LegalSection>

      <LegalSection title="Eligibility" testId="terms-section-eligibility">
        <p>
          841 is intended for users aged 16 and above and is not directed to children under 16. By
          using the service, you confirm that you meet this requirement.
        </p>
      </LegalSection>

      <LegalSection title="Subscriptions and billing" testId="terms-section-billing">
        <p>
          When 841 launches, membership will be available at ₹499 per month, beginning with a
          1-week free trial. Payments are processed by the applicable payment provider, and final
          pricing and billing terms will always be shown clearly before any payment is taken.
          Joining the waitlist on this website is free and creates no payment obligation.
        </p>
      </LegalSection>

      <LegalSection title="Your content" testId="terms-section-content">
        <p>
          Journal entries, reflections and other content you create remain yours. You allow 841 to
          store and process that content so we can provide the features you use. You may request
          deletion of your content as described in our Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection title="Acceptable use" testId="terms-section-acceptable-use">
        <p>
          Please use 841 lawfully and kindly — do not misuse the service, attempt to access systems
          or data that are not yours, or interfere with other users' experience.
        </p>
      </LegalSection>

      <LegalSection title="Intellectual property" testId="terms-section-ip">
        <p>
          The 841 name, logo, design and content belong to 841 Wellness and may not be reproduced
          without permission.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of liability" testId="terms-section-liability">
        <p>
          To the extent permitted by law, 841 is not liable for decisions you make based on general
          wellness guidance, or for indirect or consequential losses arising from use of the
          service. Nothing in these terms excludes liability that cannot be excluded by law.
        </p>
      </LegalSection>

      <LegalSection title="Changes to these terms" testId="terms-section-changes">
        <p>
          We may update these terms from time to time. The current version will always be available
          on this page, with its effective date.
        </p>
      </LegalSection>

      <LegalSection title="Contact" testId="terms-section-contact">
        <p>
          Questions about these terms? Reach us through the Support page or at
          <strong> support@the841.com</strong>. For privacy and data matters, write to
          <strong> privacy@the841.com</strong>.
        </p>
      </LegalSection>
    </PageShell>
  );
}
